import React from 'react';
import { clsx } from 'clsx';

import { formatCurrency } from '@/utils/format';
import { formatDecimalString } from '@/utils/decimal-display';
import { cobConvertToTarget } from '@/services/currency/cob-fx-service';
import { useFxRatesStore } from '@/stores/trades/fx-rates-store';

/**
 * Cash-pool sections of the Collateral Manager workspace (LR-587, product
 * answers #4/#5 of 12.08): the book is separated per pool — all USD pools
 * first, then EUR, and so on — each with its high-level figures (balance,
 * trade count, current exposure and, on GMRA, the indicative net call).
 *
 * A card FOCUSES the workspace on its pool: the grid, the aggregate bar, the
 * selection, the export and the Run-MTM / margin-call scope all narrow to that
 * pool's trades, so "process amounts per cashpool" is the ordinary flow —
 * focus, review, post — and the authoritative figures still come from the
 * preview dialog. "All pools" (or clicking the focused card again) widens back
 * to the whole book for a bulk run.
 *
 * The net-call figure here is INDICATIVE (client-side Σ exposure − ledger
 * balance, both COB-based): the post dialog recomputes the authoritative
 * net-of-pool figure server-side. Rows that predate the mandatory pool
 * (LR-586) group under "No cash pool" — the backend refuses to post their
 * collateral until a pool is assigned, so the section names the fix rather
 * than hiding the rows.
 *
 * It is shown ONLY when the two operands are the same money over the same set of
 * trades. Exposure is native to the SECURITY currency and a pool is denominated in
 * its agreement's currency, so a trade in another currency is converted at COB, as
 * the server does for the call — and without a COB rate it cannot be compared at
 * all. An unknown balance is not a zero one, and a sidebar filter that hides part
 * of the pool's book shrinks only the exposure. In every
 * such case the card says nothing rather than asserting a direction: "Due to us"
 * against "Due to CP" is which way cash moves, and it is the first thing read
 * before posting.
 */

export interface MtmPoolSection {
  /** String(pool id), or 'none' for rows without a pool. */
  key: string;
  name: string;
  /** The pool's currency (its agreement's); null when the pool could not be read. */
  currency: string | null;
  /** Server-rendered label, e.g. "SOFR+25bps" or "4.5%"; null for 'none'. */
  rateDefinition: string | null;
  /** Ledger balance in the pool currency (DRF Decimal string); null when unknown. */
  balance: string | null;
  tradeCount: number;
  /** Σ getTradeExposure per TRADE currency; summed only after conversion to the pool's. */
  exposureByCcy: Map<string, number>;
  /**
   * The sidebar filters hid part of THIS pool's book, so `exposureByCcy` covers
   * only the visible slice while `balance` is still the whole ledger — the two
   * are no longer comparable and the net call is withheld.
   */
  partiallyFiltered: boolean;
}

interface PoolSectionsProps {
  sections: MtmPoolSection[];
  focusedKey: string | null;
  onFocus: (key: string | null) => void;
  /** GMRA shows the indicative net call; GMSLA legs only balance/exposure. */
  showNetCall: boolean;
  /** The registry read failed: balances and rates are unknown, not absent. */
  poolsUnavailable?: boolean;
}

const netCallLabel = (net: number): string => {
  if (Math.abs(net) < 0.005) return '—';
  return net > 0 ? 'Due to us' : 'Due to CP';
};

interface SectionExposure {
  ccy: string;
  amount: number;
  /** Some of it was converted from a trade currency at COB. */
  converted: boolean;
}

/** The exposure in the pool's currency, or null when a leg has no COB rate to it. */
const exposureInPoolCurrency = (section: MtmPoolSection): SectionExposure | null => {
  const pool = section.currency;
  if (!pool || section.exposureByCcy.size === 0) return null;
  let amount = 0;
  let converted = false;
  for (const [ccy, value] of section.exposureByCcy) {
    if (!ccy) return null;
    const conversion = cobConvertToTarget(value, ccy, pool);
    if (conversion.source === 'none') return null;
    amount += conversion.value;
    converted ||= ccy.toUpperCase() !== pool.toUpperCase();
  }
  return { ccy: pool, amount, converted };
};

/**
 * The section's exposure as one figure: in the pool's currency where every leg
 * converts, else in its single trade currency, else none.
 */
const sectionExposure = (section: MtmPoolSection): SectionExposure | null => {
  const inPool = exposureInPoolCurrency(section);
  if (inPool) return inPool;
  if (section.exposureByCcy.size !== 1) return null;
  const [ccy, amount] = [...section.exposureByCcy.entries()][0];
  return ccy ? { ccy, amount, converted: false } : null;
};

export const PoolSections: React.FC<PoolSectionsProps> = ({
  sections,
  focusedKey,
  onFocus,
  showNetCall,
  poolsUnavailable = false,
}) => {
  // COB rates land after the first render; subscribing re-converts the exposure then.
  useFxRatesStore((s) => s.version);
  if (sections.length === 0) return null;

  return (
    <div
      className="scroll-x-thin flex items-stretch gap-2 overflow-x-auto border-t border-gray-200 bg-slate-50 px-4 py-2"
      role="group"
      aria-label="Cash pools"
    >
      {poolsUnavailable && (
        // A failed registry read is not an empty registry: without this the cards
        // fall back to row data, print no balance and no rate, and read as "these
        // pools are empty" on the screen that posts collateral.
        <span
          role="alert"
          className="shrink-0 self-center rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700"
        >
          Pool balances unavailable — retry before posting.
        </span>
      )}
      <button
        type="button"
        onClick={() => onFocus(null)}
        aria-pressed={focusedKey === null}
        className={clsx(
          'shrink-0 self-center rounded-md border px-3 py-1.5 text-xs font-medium motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          focusedKey === null
            ? 'border-blue-600 bg-blue-50 text-blue-700'
            : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
        )}
      >
        All pools
      </button>

      {sections.map((section) => {
        const isFocused = focusedKey === section.key;
        const exposure = sectionExposure(section);
        // The net call is only meaningful when both operands are the same money:
        // exposure in the pool's own currency, a balance we actually read,
        // and the SAME set of trades on both sides. Anything else renders "—" rather
        // than a coloured direction — an unknown balance substituted with zero used
        // to claim "Due to us" for a fully collateralised pool, and a sidebar filter
        // that hid part of this pool's book shrank the exposure while leaving the
        // whole ledger balance to subtract, which flips the direction outright. The
        // DISPLAYED balance never goes through a float (see `formatDecimalString`
        // below); this parse feeds only the indicative label, whose "—" band is a
        // whole half-cent wide. A converted exposure gets no direction either: it prices
        // the trades at their last mark, not at the COB the call uses, and the
        // conversion turned that gap into a direction the call panel contradicts.
        const net =
          section.balance !== null &&
          exposure !== null &&
          exposure.ccy === section.currency &&
          !exposure.converted &&
          !section.partiallyFiltered
            ? exposure.amount - Number(section.balance)
            : null;
        return (
          <button
            key={section.key}
            type="button"
            onClick={() => onFocus(isFocused ? null : section.key)}
            aria-pressed={isFocused}
            className={clsx(
              'shrink-0 rounded-md border px-3 py-1.5 text-left motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              isFocused
                ? 'border-blue-600 bg-blue-50'
                : 'border-slate-300 bg-white hover:bg-slate-100'
            )}
          >
            <span className="flex items-baseline gap-2">
              <span className="text-xs font-semibold text-slate-900">
                {section.name}
              </span>
              {section.currency && (
                <span className="text-[11px] font-medium text-slate-500">
                  {section.currency}
                  {section.rateDefinition ? ` · ${section.rateDefinition}` : ''}
                </span>
              )}
            </span>
            <span className="mt-0.5 flex items-baseline gap-3 text-[11px] text-slate-600">
              <span>
                {section.tradeCount} trade{section.tradeCount === 1 ? '' : 's'}
              </span>
              {section.balance !== null && section.currency && (
                <span>
                  Pool{' '}
                  {formatDecimalString(section.balance, {
                    currency: section.currency,
                  })}
                </span>
              )}
              <span
                title={
                  exposure?.converted
                    ? `Trades in other currencies converted to ${exposure.ccy} at COB.`
                    : undefined
                }
              >
                {/* Labelled with the currency it is actually in, and left unstated
                    when the legs could not be brought to one. */}
                Exp{' '}
                {exposure?.converted && (
                  <>
                    {/* The card is a button, so no badge inside: the mark is visible, the
                        sentence is read with the figure. */}
                    <span aria-hidden>≈ </span>
                    <span className="sr-only">
                      Trades in other currencies converted to {exposure.ccy} at COB:{' '}
                    </span>
                  </>
                )}
                {exposure
                  ? formatCurrency(exposure.amount, exposure.ccy)
                  : section.exposureByCcy.size > 1
                    ? 'multi-ccy'
                    : '—'}
              </span>
              {showNetCall && (
                <span
                  title={
                    net !== null
                      ? 'Indicative: Σ exposure − pool balance. The post dialog recomputes the authoritative figure.'
                      : section.partiallyFiltered
                        ? 'Not comparable: the filters hide part of this pool’s book, so its exposure covers fewer trades than its balance. Clear the filters, or use the post dialog for the authoritative figure.'
                        : 'Not comparable: the pool balance is unknown, or a trade currency has no COB rate to the pool’s. The post dialog computes the authoritative figure.'
                  }
                  className={clsx(
                    'font-medium',
                    net === null || Math.abs(net) < 0.005
                      ? 'text-slate-500'
                      : net > 0
                        ? 'text-emerald-700'
                        : 'text-red-700'
                  )}
                >
                  {net === null ? '—' : netCallLabel(net)}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};
